import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Send, Plus, Trash2, PackageCheck, TruckIcon } from "lucide-react";

const GUIDE_STATUS = {
  pending: { label: "Pendente", cls: "bg-slate-500/20 text-slate-500", next: "picking", nextLabel: "Iniciar Picking" },
  picking: { label: "Em Picking", cls: "bg-amber-500/20 text-amber-600", next: "in_transit", nextLabel: "Expedir" },
  in_transit: { label: "Em Trânsito", cls: "bg-blue-500/20 text-blue-500", next: "delivered", nextLabel: "Confirmar Entrega" },
  delivered: { label: "Entregue", cls: "bg-emerald-500/20 text-emerald-600" },
  cancelled: { label: "Cancelada", cls: "bg-red-500/20 text-red-500" },
};

export default function LogisticsPage() {
  const { token } = useAuth();
  const [guides, setGuides] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [modal, setModal] = useState(false);
  const [items, setItems] = useState([{ name: "", quantity: "" }]);
  const [form, setForm] = useState({ customer_name: "", address: "", driver_id: "", vehicle_id: "", notes: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const g = await axios.get(`${API_URL}/delivery-guides`, { headers, withCredentials: true });
      setGuides(g.data);
    } catch { toast.error("Erro ao carregar guias"); }
    try {
      const d = await axios.get(`${API_URL}/delivery-guides/drivers`, { headers, withCredentials: true });
      setDrivers(d.data);
    } catch { /* sem permissão para motoristas */ }
    try {
      const v = await axios.get(`${API_URL}/fleet/vehicles`, { headers, withCredentials: true });
      setVehicles(v.data);
    } catch { /* sem permissão para frota */ }
  };

  const createGuide = async (e) => {
    e.preventDefault();
    const validItems = items.filter((i) => i.name && i.quantity).map((i) => ({ name: i.name, quantity: parseFloat(i.quantity) }));
    if (validItems.length === 0) { toast.error("Adicione pelo menos um item"); return; }
    try {
      await axios.post(`${API_URL}/delivery-guides`, {
        ...form,
        driver_id: form.driver_id || null,
        vehicle_id: form.vehicle_id || null,
        items: validItems,
      }, { headers, withCredentials: true });
      toast.success("Guia de entrega criada");
      setModal(false); setItems([{ name: "", quantity: "" }]); setForm({ customer_name: "", address: "", driver_id: "", vehicle_id: "", notes: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const updateStatus = async (guide, status) => {
    try {
      await axios.put(`${API_URL}/delivery-guides/${guide.guide_id}/status?status=${status}`, {}, { headers, withCredentials: true });
      toast.success("Estado atualizado");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="logistics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Logística & Expedições</h1>
          <p className="text-muted-foreground">Guias de entrega, picking e expedição com motorista e veículo</p>
        </div>
        <Button onClick={() => setModal(true)} data-testid="add-guide-btn"><Plus className="w-4 h-4 mr-2" /> Nova Guia</Button>
      </div>

      {guides.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Send className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhuma guia de entrega</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {guides.map((g) => {
            const st = GUIDE_STATUS[g.status] || {};
            return (
              <Card key={g.guide_id} data-testid={`guide-${g.guide_id}`}>
                <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-mono font-semibold">{g.number} <span className="font-sans font-normal">— {g.customer_name}</span></p>
                    <p className="text-xs text-muted-foreground">
                      {(g.items || []).length} item(ns)
                      {g.driver_name && ` · Motorista: ${g.driver_name}`}
                      {g.vehicle_plate && ` · Veículo: ${g.vehicle_plate}`}
                      {g.address && ` · ${g.address}`}
                      {` · ${formatDate(g.created_at)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={st.cls}>{st.label || g.status}</Badge>
                    {st.next && (
                      <Button size="sm" onClick={() => updateStatus(g, st.next)} data-testid={`guide-next-${g.guide_id}`}>
                        {st.next === "delivered" ? <PackageCheck className="w-4 h-4 mr-1" /> : <TruckIcon className="w-4 h-4 mr-1" />}
                        {st.nextLabel}
                      </Button>
                    )}
                    {["pending", "picking"].includes(g.status) && (
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => updateStatus(g, "cancelled")}>Cancelar</Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nova Guia de Entrega</DialogTitle></DialogHeader>
          <form onSubmit={createGuide} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Cliente</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="guide-customer-input" /></div>
              <div className="space-y-1"><Label>Morada de Entrega</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label>Motorista</Label>
                <Select value={form.driver_id} onValueChange={(v) => setForm({ ...form, driver_id: v })}>
                  <SelectTrigger data-testid="guide-driver-select"><SelectValue placeholder={drivers.length ? "Escolher" : "Sem motoristas"} /></SelectTrigger>
                  <SelectContent>{drivers.map((d) => <SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Veículo</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                  <SelectTrigger data-testid="guide-vehicle-select"><SelectValue placeholder={vehicles.length ? "Escolher" : "Sem veículos"} /></SelectTrigger>
                  <SelectContent>{vehicles.map((v) => <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.plate} {v.brand ? `(${v.brand})` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Label>Itens</Label>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {items.map((it, i) => (
                <div key={i} className="flex gap-2">
                  <Input className="flex-1" placeholder="Descrição *" value={it.name} onChange={(e) => { const arr = [...items]; arr[i].name = e.target.value; setItems(arr); }} />
                  <Input className="w-24" type="number" step="0.01" min="0.01" placeholder="Qtd *" value={it.quantity} onChange={(e) => { const arr = [...items]; arr[i].quantity = e.target.value; setItems(arr); }} />
                  <Button type="button" size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" onClick={() => setItems([...items, { name: "", quantity: "" }])}><Plus className="w-4 h-4 mr-1" /> Item</Button>
            <div className="space-y-1"><Label>Notas</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-guide-btn">Criar Guia</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
